// First-party cookies for keeptabs.app and the visitor's cookie consent.
// Same names and rules as mytabs.app (mytabs-customer-website
// src/views/Discover/cookies.js):
//
//   tabs_consent  strictly necessary. "all" or "necessary": the visitor's
//                 answer to the cookie banner, kept for a year.
//   tabs_theme    functional. "light" or "dark", written only after the
//                 visitor accepts all cookies.
//
// Consent changes are broadcast so the banner and the theme hook stay in sync
// across the page.

import { useCallback, useEffect, useState } from "react";

export const CONSENT_COOKIE = "tabs_consent";
export const THEME_COOKIE = "tabs_theme";
const CONSENT_EVENT = "tabs-consent-change";
const OPEN_EVENT = "tabs-consent-open";
const ONE_YEAR_DAYS = 365;

export function getCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((c) => c.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

export function setCookie(name, value, days = ONE_YEAR_DAYS) {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${days * 86400}; Path=/; SameSite=Lax${secure}`;
}

export function deleteCookie(name) {
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

/** "all", "necessary", or null when the visitor hasn't answered yet. */
export function readConsent() {
  const v = getCookie(CONSENT_COOKIE);
  return v === "all" || v === "necessary" ? v : null;
}

export function preferencesAllowed() {
  return readConsent() === "all";
}

/** Reopens the banner (e.g. a "Cookie settings" link). */
export function openCookieSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function useCookieConsent() {
  const [consent, setConsent] = useState(readConsent);
  const [open, setOpen] = useState(() => readConsent() === null);

  useEffect(() => {
    const onChange = (e) => setConsent(e.detail);
    const onOpen = () => setOpen(true);
    window.addEventListener(CONSENT_EVENT, onChange);
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => {
      window.removeEventListener(CONSENT_EVENT, onChange);
      window.removeEventListener(OPEN_EVENT, onOpen);
    };
  }, []);

  const choose = useCallback((value) => {
    setCookie(CONSENT_COOKIE, value);
    if (value !== "all") deleteCookie(THEME_COOKIE);
    setOpen(false);
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }));
  }, []);

  return { consent, open, choose };
}

export const CONSENT_CHANGE_EVENT = CONSENT_EVENT;
